import { Eye, FileText, Pencil, Send, Trash2 } from 'lucide-react'

import QuoteStatusBadge from './QuoteStatusBadge'

export default function QuoteColumns(
  onView,
  onSend,
  showSalesPerson = false,
  onEdit,
  onDelete,
) {
  const columns = [
    {
      id: 'quoteNo',
      header: 'QUOTE #',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-slate-100">
            <FileText className="size-5 text-slate-600" />
          </div>

          <span className="font-semibold text-slate-900">{row.quoteNo}</span>
        </div>
      ),
    },
    {
      id: 'contactName',
      header: 'CUSTOMER / CONTACT',
      className: 'max-w-[220px] min-w-[150px] whitespace-normal',
      cell: (row) => (
        <div className="min-w-0 max-w-[220px]">
          <p
            className="font-medium text-slate-900 break-words line-clamp-2"
            title={row.accountName || ''}
          >
            {row.accountName || '-'}
          </p>
          <p
            className="text-xs text-slate-500 break-words line-clamp-1"
            title={row.contactName || row.customer || '-'}
          >
            {row.contactName || row.customer || '-'}
          </p>
        </div>
      ),
    },
  ]

  if (showSalesPerson) {
    columns.push({
      id: 'salesPerson',
      header: 'SALES PERSON',
      className: 'max-w-[160px] min-w-[120px] whitespace-normal',
      cell: (row) => {
        const spName = row.createdBy?.name || row.salesPerson || '-'
        return (
          <span
            className="font-medium text-slate-800 break-words line-clamp-1"
            title={spName}
          >
            {spName}
          </span>
        )
      },
    })
  }

  columns.push(
    {
      id: 'product',
      header: 'PRODUCT',
      className: 'max-w-[220px] min-w-[150px] whitespace-normal',
      cell: (row) => {
        const products = row.products || []
        if (!products.length) {
          const fallback = row.product || '-'
          return (
            <span
              className="font-medium text-slate-800 break-words line-clamp-2"
              title={fallback}
            >
              {fallback}
            </span>
          )
        }
        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const first = isPartialDesc
          ? productsWithDesc[0]?.product || '-'
          : products[0]?.product || '-'
        const rest = products.length - 1
        return (
          <div className="flex items-center gap-1.5 max-w-[220px] min-w-0">
            <span
              className="font-medium text-slate-800 break-words line-clamp-2 min-w-0"
              title={first}
            >
              {first}
            </span>
            {rest > 0 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onView?.(row)
                }}
                title="Click to view all products"
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 hover:border-indigo-300 cursor-pointer"
              >
                +{rest} more
              </button>
            ) : null}
            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(row)
                }}
                title="Edit Quotation Products"
                className="inline-flex shrink-0 items-center rounded p-1 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition"
              ></button>
            )}
          </div>
        )
      },
    },
    {
      id: 'description',
      header: 'DESCRIPTION',
      className: 'max-w-[220px] min-w-[150px] whitespace-normal',
      cell: (row) => {
        const products = row.products || []
        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const sourceList = isPartialDesc ? productsWithDesc : products
        const descriptionsWithProduct = sourceList
          .map((p) => ({
            product: p.product || '',
            description:
              typeof p.description === 'string' ? p.description.trim() : '',
          }))
          .filter((item) => Boolean(item.description))

        if (!descriptionsWithProduct.length && row.description?.trim()) {
          descriptionsWithProduct.push({
            product: '',
            description: row.description.trim(),
          })
        }

        if (!descriptionsWithProduct.length) {
          return <span className="text-slate-400">-</span>
        }

        const firstDesc = descriptionsWithProduct[0].description
        const restDescCount = descriptionsWithProduct.length - 1

        const tooltip = descriptionsWithProduct
          .map((item) =>
            item.product
              ? `${item.product}: ${item.description}`
              : item.description,
          )
          .join('\n')

        return (
          <div className="flex items-center gap-1.5 max-w-[220px] min-w-0">
            <span
              className="text-xs text-slate-600 break-words line-clamp-2 min-w-0"
              title={tooltip}
            >
              {firstDesc}
            </span>
            {restDescCount > 0 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onView?.(row)
                }}
                title="Click to view all descriptions"
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 hover:border-indigo-300 cursor-pointer"
              >
                +{restDescCount} more
              </button>
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'qty',
      header: 'QTY',
      className: 'font-semibold',
      cell: (row) => {
        const products = row.products || []
        if (!products.length) return row.qty ?? '-'
        const totalQty = products.reduce(
          (sum, p) => sum + (Number(p.quantity) || 0),
          0,
        )
        return totalQty || '-'
      },
    },
    {
      id: 'grandTotal',
      header: 'TOTAL (₹)',
      className: 'font-semibold',
      cell: (row) => `₹${(row.grandTotal || 0).toLocaleString('en-IN')}`,
    },
    {
      id: 'issued',
      header: 'ISSUED',
      cell: (row) =>
        row.quoteDate
          ? new Date(row.quoteDate).toLocaleDateString()
          : row.issued || '-',
    },
    {
      id: 'expires',
      header: 'EXPIRES',
      cell: (row) =>
        row.validUntil
          ? new Date(row.validUntil).toLocaleDateString()
          : row.expires || '-',
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => <QuoteStatusBadge status={row.status || 'draft'} />,
    },
    {
      id: 'action',
      header: 'ACTION',
      className: 'text-right',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => onSend(row)}
            title="Send Quotation"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700"
          >
            <Send className="size-4" />
            Send
          </button>

          <button
            onClick={() => onView(row)}
            title="View Details"
            className="rounded-full p-2 transition hover:bg-slate-100"
          >
            <Eye className="size-4 text-slate-600" />
          </button>

          {onEdit && (
            <button
              onClick={() => onEdit(row)}
              title="Edit Quotation"
              className="rounded-full p-2 transition hover:bg-indigo-50"
            >
              <Pencil className="size-4 text-indigo-500" />
            </button>
          )}

          {onDelete && (
            <button
              onClick={() => onDelete(row)}
              title="Delete Quotation"
              className="rounded-full p-2 transition hover:bg-red-50"
            >
              <Trash2 className="size-4 text-red-400" />
            </button>
          )}
        </div>
      ),
    },
  )

  return columns
}
